import { MessageType } from "../protocol/catalogue.ts";

/**
 * What a freeze lets through (ADR-056's amendment of 2026-10-04, item 2, and of 2026-10-06, decision 42), as one pair
 * of lists every side reads, so they cannot drift: the gateway drops a frozen account's frames unless their type is
 * on the pass list, the page sends no message the drop list names while its player is frozen, and the map server
 * refuses the same messages for a character it holds. A client message that goes live later is dropped until someone
 * decides otherwise: `freeze.test.ts` walks the catalogue's live client messages and fails for one on neither list.
 * The pass list is what a frozen player keeps: joining (`hello`), proving it is there (`ping`), talking (chat's
 * requests and `afk`, since a freeze is for talking to someone mid-incident), a GM's performance panel
 * (`perfRequest`, which moves nothing in the world), leaving the login queue (`queueLeave`, which a connection still
 * waiting for room sends and which ends only its own place, P1.4-T163), leaving the world by the menu (`leave`, which
 * does no more than closing the tab does, ADR-084's amendment of 2026-10-13), and standing up (`respawn`, for a character
 * that lies dead while it is frozen), and acknowledging a staff notice (`staffNoticeAck`, which moves nothing and which a
 * frozen player must still be able to press, since a warning stays on screen until it is), and ending a conversation
 * (`dialogueClose`, which only closes a window the player opened before the freeze).
 */

/**
 * The client messages a frozen account may still send.
 */
export const FREEZE_PASSES: ReadonlySet<number> = new Set<number>([
  MessageType.Hello,
  MessageType.Ping,
  MessageType.PerfRequest,
  MessageType.QueueLeave,
  MessageType.Leave,
  MessageType.Respawn,
  MessageType.StaffNoticeAck,
  MessageType.Say,
  MessageType.Whisper,
  MessageType.Block,
  MessageType.Unblock,
  MessageType.Pose,
  MessageType.NameLookup,
  MessageType.ChannelSay,
  MessageType.ChannelJoin,
  MessageType.ChannelLeave,
  MessageType.ChannelMute,
  MessageType.Afk,
  MessageType.PartyAnswer,
  MessageType.PartyLeave,
  MessageType.PartySay,
  MessageType.DialogueClose,
]);

/**
 * The live client messages a freeze was decided to drop: moving, targeting, acting, switching the PvP flag, emoting,
 * using an item, rearranging the bag, taking loot, picking up a ground item, splitting or destroying a stack, sorting the bag, keeping a stack in place, arranging the bar, interacting and choosing a line in a conversation (`dialogueChoose`, by the same default as `interact`). The gateway drops everything not on the pass list, so for it this list
 * records the decision and the test holds every live client message to one list or the other; the page and the map
 * server read it to refuse the same messages themselves. `emote` is here because nobody has ruled that a frozen
 * player may emote (ADR-114 decision 6.7), so a freeze drops it; `bagMove` is here by the same default, since nobody
 * has ruled that a frozen player may rearrange the bag (P2.8-T14), and `bagSplit`, `destroyItem`, `bagSort` and `bagKeep` with it;
 * `barSet` is here as ADR-121 decision 5.4 rules, and `pickUp` as ADR-091's amendment for P2.8-T13 rules (decision 3, "dropped for a
 * frozen account"). The three travel requests are here as ADR-137 ruling 5 refuses travel to a frozen character: a freeze
 * breaks a running channel, so `travelCancel` has nothing left to end.
 */
export const FREEZE_DROPS: ReadonlySet<number> = new Set<number>([MessageType.Input, MessageType.Target, MessageType.Act, MessageType.PvpFlagToggle, MessageType.PartyInvite, MessageType.PartyInviteLookup, MessageType.PartyRemove, MessageType.PartyPromote, MessageType.PartyGrant, MessageType.Emote, MessageType.UseItem, MessageType.BagMove, MessageType.LootTake, MessageType.PickUp, MessageType.BagSplit, MessageType.DestroyItem, MessageType.BagSort, MessageType.BagKeep, MessageType.BarSet, MessageType.ToolBeltSet, MessageType.DropItem, MessageType.Interact, MessageType.DialogueChoose, MessageType.TravelAsk, MessageType.Travel, MessageType.TravelCancel, MessageType.Harvest, MessageType.CompanionSet, MessageType.CompanionName, MessageType.SpendAttributes, MessageType.RespecAttributes]);

/**
 * Whether a frozen account's frame of a type reaches what handles it.
 *
 * @param type The message type, a text message's or a binary frame's.
 * @returns True only for a type on the pass list.
 * @example
 * passesFreeze(MessageType.Say);
 * // true: a frozen player can still talk
 */
export function passesFreeze(type: number): boolean {
  return FREEZE_PASSES.has(type);
}

/**
 * Whether a freeze was decided to drop a client message, which the page and the map server ask before they send or
 * take one for a frozen character.
 *
 * @param type The message type, a text message's or a binary frame's.
 * @returns True only for a type on the drop list; a type on neither list is not named here, though the gateway
 *   drops it.
 * @example
 * droppedByFreeze(MessageType.Act);
 * // true
 */
export function droppedByFreeze(type: number): boolean {
  return FREEZE_DROPS.has(type);
}
